import type { FileSyncState } from '../../types/sharepoint';

/**
 * Tipos de documentos permitidos para embarques
 */
export enum ShipmentFileType {
  COMMERCIAL_INVOICE = 'commercial_invoice',
  BILL_OF_LADING = 'bill_of_lading',
  BOOKING = 'booking',
  PACKING_LIST = 'packing_list',
  PURCHASE_ORDER = 'purchase_order',
  THIRD_PARTY_INVOICE = 'third_party_invoice',
  FREIGHT_AGENT_INVOICE = 'freight_agent_invoice',
  CUSTOMS_AGENT_INVOICE = 'customs_agent_invoice',
  HOUSE_BILL_OF_LADING = 'house_bill_of_lading',
  // Documentos aéreos
  MASTER_AIR_WAYBILL = 'master_air_waybill',
  HOUSE_AIR_WAYBILL = 'house_air_waybill',
  // Documentos específicos OTM
  OTM_INSTRUCTIONS = 'otm_instructions',
  FREIGHT_CERTIFICATION = 'freight_certification',
  INSURANCE_CERTIFICATE = 'insurance_certificate',
  MSDS = 'msds',
  TECHNICAL_SHEET = 'technical_sheet',
  QUOTATION = 'quotation',
  SERIALS = 'serials',
  CARGO_MANIFEST = 'cargo_manifest',
}

export interface DocumentReviewInfo {
  uid: string;
  name: string;
  lastName: string;
  reviewedAt: Date;
}

/**
 * Interfaz para archivos de embarque
 */
export interface ShipmentFile {
  /** ID único del archivo */
  id: string;
  
  /** ID del embarque al que pertenece */
  shipmentId: string;
  
  /** Tipo de documento - puede ser un tipo predefinido o personalizado */
  fileType: string;

  /**
   * Tipos adicionales que el mismo archivo contiene (documento combinado,
   * p. ej. ['packing_list'] en una factura comercial + lista de empaque).
   * El tipo principal sigue siendo `fileType`.
   */
  additionalFileTypes?: string[];

  /** Nombre original del archivo */
  fileName: string;

  /**
   * Nombre que se muestra en pantalla, si el usuario renombró el documento.
   * Solo es visual: `fileName` y el objeto en Storage no cambian.
   */
  displayName?: string;

  /** URL del archivo en Firebase Storage */
  fileUrl: string;

  /** Ruta del objeto en Firebase Storage (lo que identifica el archivo al copiarlo a una OC) */
  storagePath?: string;

  /** Motivo que deja la extracción cuando falla (status 'failed') */
  error?: string;
  
  /** Tamaño del archivo en bytes */
  fileSize: number;
  
  /** Tipo MIME del archivo */
  mimeType: string;
  
  /** Fecha de carga */
  uploadedAt: Date;
  
  /** Usuario que cargó el archivo */
  uploadedBy: {
    id: string;
    name: string;
    lastName?: string;
  };
  
  /** Compañía a la que pertenece */
  companyId: string;
  
  /** Datos extraídos del documento (si se procesó con IA) */
  extractedData?: any;

  /**
   * Extracciones adicionales cuando un mismo archivo contiene más de un documento
   * (p. ej. factura comercial + packing list), indexadas por tipo de documento.
   * extractedData conserva la del tipo principal (fileType).
   */
  additionalExtractedData?: Record<string, any>;

  reviewedBy?: DocumentReviewInfo | null;

  /** ID de la carpeta a la que pertenece (null = raíz) */
  folderId?: string | null;

  pendingObservationCount?: number;

  /** Estado del procesamiento del archivo (p. ej. 'completed', 'undone'). */
  status?: string;

  /**
   * Presente solo si la extracción de este archivo todavía se puede deshacer.
   * Al deshacerla, el backend borra este campo del documento.
   */
  undoSummary?: {
    extractionId: string;
    fieldCount: number;
    createdRowCount: number;
  };

  /** Estado del espejo en SharePoint. Solo existe en compañías con la sincronización activa. */
  sharepoint?: FileSyncState;
}

/**
 * Interfaz para crear un nuevo archivo
 */
export interface CreateShipmentFileDTO {
  shipmentId: string;
  fileType: string;
  /** Tipos adicionales del documento combinado (ver ShipmentFile.additionalFileTypes) */
  additionalFileTypes?: string[];
  fileName: string;
  file: File;
  companyId: string;
  uploadedBy: {
    id: string;
    name: string;
    lastName?: string;
  };
  folderId?: string | null;
}

export interface ShipmentDocumentFolder {
  id: string;
  name: string;
  parentFolderId: string | null;
  shipmentId: string;
  createdBy: string;
  createdAt: any;
}

/**
 * Labels para los tipos de documentos
 */
export const SHIPMENT_FILE_TYPE_LABELS: Record<ShipmentFileType, { es: string; en: string }> = {
  [ShipmentFileType.COMMERCIAL_INVOICE]: {
    es: 'Factura Comercial',
    en: 'Commercial Invoice'
  },
  [ShipmentFileType.THIRD_PARTY_INVOICE]: {
    es: 'Factura de Terceros',
    en: 'Third Party Invoice'
  },
  [ShipmentFileType.FREIGHT_AGENT_INVOICE]: {
    es: 'Factura agente de carga',
    en: 'Freight Forwarder Invoice'
  },
  [ShipmentFileType.CUSTOMS_AGENT_INVOICE]: {
    es: 'Factura agente de aduana',
    en: 'Customs Broker Invoice'
  },
  [ShipmentFileType.BILL_OF_LADING]: {
    es: 'Conocimiento de Embarque',
    en: 'Bill of Lading'
  },
  [ShipmentFileType.HOUSE_BILL_OF_LADING]: {
    es: 'Conocimiento de Embarque (HBL)',
    en: 'House Bill of Lading'
  },
  [ShipmentFileType.MASTER_AIR_WAYBILL]: {
    es: 'Guía Aérea Master (MAWB)',
    en: 'Master Air Waybill (MAWB)'
  },
  [ShipmentFileType.HOUSE_AIR_WAYBILL]: {
    es: 'Guía Aérea House (HAWB)',
    en: 'House Air Waybill (HAWB)'
  },
  [ShipmentFileType.BOOKING]: {
    es: 'Reserva',
    en: 'Booking'
  },
  [ShipmentFileType.PACKING_LIST]: {
    es: 'Lista de Empaque',
    en: 'Packing List'
  },
  [ShipmentFileType.PURCHASE_ORDER]: {
    es: 'Orden de Compra',
    en: 'Purchase Order'
  },
  [ShipmentFileType.OTM_INSTRUCTIONS]: {
    es: 'Instrucciones OTM / Solicitud de Servicio',
    en: 'OTM Instructions / Service Request'
  },
  [ShipmentFileType.FREIGHT_CERTIFICATION]: {
    es: 'Certificación de Fletes',
    en: 'Freight Certification'
  },
  [ShipmentFileType.INSURANCE_CERTIFICATE]: {
    es: 'Certificado de Seguro',
    en: 'Insurance Certificate'
  },
  [ShipmentFileType.MSDS]: {
    es: 'Hoja de Seguridad (MSDS)',
    en: 'Safety Data Sheet (MSDS)'
  },
  [ShipmentFileType.TECHNICAL_SHEET]: {
    es: 'Ficha Técnica de Mercancía',
    en: 'Technical Data Sheet'
  },
  [ShipmentFileType.QUOTATION]: {
    es: 'Cotización',
    en: 'Quotation'
  },
  [ShipmentFileType.SERIALS]: {
    es: 'Seriales',
    en: 'Serial Numbers'
  },
  [ShipmentFileType.CARGO_MANIFEST]: {
    es: 'Manifiesto de Carga',
    en: 'Cargo Manifest'
  }
};

/**
 * Iconos para cada tipo de documento
 */
export const SHIPMENT_FILE_TYPE_ICONS: Record<ShipmentFileType, string> = {
  [ShipmentFileType.COMMERCIAL_INVOICE]: 'DocumentTextIcon',
  [ShipmentFileType.THIRD_PARTY_INVOICE]: 'DocumentTextIcon',
  [ShipmentFileType.FREIGHT_AGENT_INVOICE]: 'DocumentTextIcon',
  [ShipmentFileType.CUSTOMS_AGENT_INVOICE]: 'DocumentTextIcon',
  [ShipmentFileType.BILL_OF_LADING]: 'DocumentChartBarIcon',
  [ShipmentFileType.BOOKING]: 'ClipboardDocumentListIcon',
  [ShipmentFileType.PACKING_LIST]: 'DocumentCheckIcon',
  [ShipmentFileType.PURCHASE_ORDER]: 'DocumentTextIcon',
  [ShipmentFileType.HOUSE_BILL_OF_LADING]: 'DocumentChartBarIcon',
  [ShipmentFileType.MASTER_AIR_WAYBILL]: 'PaperAirplaneIcon',
  [ShipmentFileType.HOUSE_AIR_WAYBILL]: 'PaperAirplaneIcon',
  [ShipmentFileType.OTM_INSTRUCTIONS]: 'ClipboardDocumentListIcon',
  [ShipmentFileType.FREIGHT_CERTIFICATION]: 'DocumentCurrencyDollarIcon',
  [ShipmentFileType.INSURANCE_CERTIFICATE]: 'ShieldCheckIcon',
  [ShipmentFileType.MSDS]: 'ExclamationTriangleIcon',
  [ShipmentFileType.TECHNICAL_SHEET]: 'DocumentMagnifyingGlassIcon',
  [ShipmentFileType.QUOTATION]: 'DocumentCurrencyDollarIcon',
  [ShipmentFileType.SERIALS]: 'QueueListIcon',
  [ShipmentFileType.CARGO_MANIFEST]: 'ClipboardDocumentListIcon',
};

/**
 * Función helper para formatear el tamaño del archivo
 */
export const formatFileSize = (bytes: number | null | undefined): string => {
  if (bytes == null || isNaN(bytes)) return '-';
  if (bytes === 0) return '0 Bytes';
  
  if (bytes < 0) return '-';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  // Clamp: con bytes >= 1 TB el índice se salía del array y mostraba "undefined"
  const i = Math.min(sizes.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Función helper para obtener el icono según el tipo MIME
 */
export const getFileIcon = (mimeType: string): string => {
  if (mimeType.includes('pdf')) return 'DocumentTextIcon';
  if (mimeType.includes('image')) return 'PhotoIcon';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'TableCellsIcon';
  if (mimeType.includes('word') || mimeType.includes('document')) return 'DocumentIcon';
  return 'DocumentIcon';
};

/**
 * Función helper para verificar si un archivo se puede previsualizar
 */
export const canPreviewFile = (mimeType: string): boolean => {
  return mimeType.includes('pdf') || mimeType.includes('image');
};
